import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  Platform,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import Animated, { FadeInDown } from "react-native-reanimated";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";

import { router, useLocalSearchParams } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import secureStorage from "../../../../utils/secureStorage";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useDispatch, useSelector } from "react-redux";
import { authActions } from "../../../../store/slices/authSlice";
import { RootState } from "../../../../store/index";
import i18n from "../../../../locales/i18n";
import customAxios from "../../../../utils/customAxios";
import WebVerifyEmailScreen from "../../../../components/web/WebVerifyEmailScreen";

export default function VerifyEmailScreen() {
  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebVerifyEmailScreen />;
  }

  const locale = i18n.locale;
  const dispatch = useDispatch();
  const { email: paramEmail, type } = useLocalSearchParams();

  // Get user data and pending verification email from Redux store
  const { user, pendingVerificationEmail } = useSelector(
    (state: RootState) => state.auth
  );

  const [verificationCode, setVerificationCode] = useState("");
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [resendSpinner, setResendSpinner] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [initialCodeSent, setInitialCodeSent] = useState(false);
  const [verificationType, setVerificationType] = useState<string | null>(null);

  // Get email from various sources on component mount
  useEffect(() => {
    const loadUserEmail = async () => {
      try {
        let email = null;

        // Priority order: Redux pendingVerificationEmail > paramEmail > stored email > user email
        if (pendingVerificationEmail) {
          email = pendingVerificationEmail;
        } else if (paramEmail) {
          email = paramEmail as string;
          // Store in Redux for future use
          dispatch(authActions.setPendingVerificationEmail(email));
        } else {
          // Check stored email from previous session
          const stored = await AsyncStorage.getItem("pendingVerificationEmail");
          if (stored) {
            email = stored;
            dispatch(authActions.setPendingVerificationEmail(email));
          } else if (user?.email) {
            // Get from Redux store if user has email (for login flow)
            email = user.email;
            dispatch(authActions.setPendingVerificationEmail(email));
          }
        }

        setUserEmail(email);

        // Set verification type
        if (type) {
          setVerificationType(type as string);
        }
      } catch (error) {
        // silently ignore
      }
    };

    loadUserEmail();
  }, [paramEmail, user, pendingVerificationEmail, dispatch, type]);

  // Automatically send verification code when email is loaded (only for email verification, not password reset)
  useEffect(() => {
    const sendInitialCode = async () => {
      if (
        userEmail &&
        !initialCodeSent &&
        verificationType !== "password-reset"
      ) {
        setInitialCodeSent(true);
        try {
          const response = await customAxios.post(
            `/users/resend-verification`,
            {
              email: userEmail,
            }
          );

          Toast.show(response.data.message || i18n.t("verificationCodeSent"), {
            type: "success",
            duration: 4000,
          });
        } catch (error: any) {
          // Don't show error toast for initial send, user can manually resend
        }
      }
    };

    sendInitialCode();
  }, [userEmail, initialCodeSent, verificationType]);

  const handleVerifyEmail = async () => {
    if (!verificationCode || verificationCode.trim() === "") {
      Toast.show(i18n.t("enterVerificationCode"), {
        type: "danger",
      });
      return;
    }

    setButtonSpinner(true);
    try {
      if (!userEmail) {
        Toast.show(i18n.t("emailRequired"), {
          type: "danger",
        });
        setButtonSpinner(false);
        return;
      }

      // Use different endpoint based on verification type
      const endpoint =
        verificationType === "password-reset"
          ? `/users/verify-email` // Use the same endpoint but with different flow
          : `/users/verify-email`;

      const response = await customAxios.post(endpoint, {
        email: userEmail,
        verificationCode: verificationCode.trim(),
      });

      // Store tokens after successful verification (both email verification and password reset)
      if (response.data.accessToken && response.data.refreshToken) {
        await AsyncStorage.setItem("accessToken", response.data.accessToken);
        await secureStorage.setItemAsync(
          "refreshToken",
          response.data.refreshToken
        );

        // Now fetch complete user details like in login flow
        try {
          const userDetailsResponse = await customAxios.get("/users/check");

          // Set complete user data from /users/check endpoint
          dispatch(authActions.setUser(userDetailsResponse.data?.userinfo));
        } catch (userError) {
          // Fallback: set basic user data from verification response
          const userData = {
            id: response.data?.userinfo?.id,
            username: response.data?.userinfo?.username || response.data?.user,
            email: userEmail,
            payed: response.data?.payed || false,
            verified: true,
          };

          dispatch(authActions.setUser(userData));
        }
      }

      // Clear pending verification email from Redux and storage
      dispatch(authActions.clearPendingVerificationEmail());
      await AsyncStorage.removeItem("pendingVerificationEmail");

      const successMessage =
        verificationType === "password-reset"
          ? i18n.t("passwordResetSuccess")
          : i18n.t("emailVerified");

      Toast.show(response.data.message || successMessage, {
        type: "success",
      });

      setButtonSpinner(false);

      // Both email verification and password reset lead to start screen
      // because API returns access/refresh tokens for both
      router.replace("/(drawer)/start");
    } catch (error: any) {
      setButtonSpinner(false);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("emailVerificationFailed");

      Toast.show(errorMessage, {
        type: "danger",
      });
    }
  };

  const handleResendCode = async () => {
    setResendSpinner(true);
    try {
      if (!userEmail) {
        Toast.show(i18n.t("emailRequired"), {
          type: "danger",
        });
        setResendSpinner(false);
        return;
      }

      // Use different endpoint based on verification type
      let endpoint, requestBody;

      if (verificationType === "password-reset") {
        // For password reset, resend the forgot password request
        endpoint = `/users/forgot-password/`;
        requestBody = { email: userEmail };
      } else {
        // For email verification, use resend verification
        endpoint = `/users/resend-verification`;
        requestBody = { email: userEmail };
      }

      const response = await customAxios.post(endpoint, requestBody);

      Toast.show(response.data.message || i18n.t("verificationCodeResent"), {
        type: "success",
      });

      setResendSpinner(false);
    } catch (error: any) {
      setResendSpinner(false);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("resendVerificationFailed");

      Toast.show(errorMessage, {
        type: "danger",
      });
    }
  };

  return (
    <LinearGradient
      colors={["#004E6D", "#0596D0"]}
      locations={[0, 1]}
      style={{ flex: 1 }}
    >
      {/* Back Button */}
      <TouchableOpacity
        onPress={() => router.back()}
        style={{
          position: "absolute",
          top: hp("6%"),
          left: wp("5%"),
          zIndex: 10,
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          borderRadius: 20,
          padding: 8,
        }}
      >
        <Feather name="arrow-left" size={24} color="white" />
      </TouchableOpacity>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Top section with logo and curved bottom */}
        <View
          style={{
            alignItems: "center",
            paddingTop: hp("8%"),
            paddingBottom: hp("6%"),
          }}
        >
          <Image
            style={{
              width: wp("20%"),
              height: wp("20%"),
              tintColor: "white",
              marginBottom: hp("3%"),
            }}
            source={require("../../../../assets/logo-no-bg-white.png")}
            resizeMode="contain"
          />

          {/* White curved section */}
          <View
            style={{
              backgroundColor: "white",
              width: wp("100%"),
              minHeight: hp("70%"),
              borderTopLeftRadius: 60,
              borderTopRightRadius: 60,
              paddingHorizontal: wp("8%"),
              paddingTop: hp("4%"),
              marginTop: hp("2%"),
            }}
          >
            {/* Icon */}
            <View style={{ alignItems: "center", marginBottom: hp("3%") }}>
              <View
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 40,
                  backgroundColor: "#E3F2FD",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: hp("2%"),
                }}
              >
                <Feather name="mail" size={40} color="#0596D0" />
              </View>
            </View>

            {/* Title */}
            <Animated.View entering={FadeInDown.duration(400).delay(100)}>
              <Text
              style={{
                fontSize: 24,
                fontWeight: "bold",
                textAlign: "center",
                color: "#004E6D",
                marginBottom: hp("2%"),
                fontFamily: "kawkabBold",
              }}
            >
              {verificationType === "password-reset"
                ? i18n.t("verifyPasswordReset")
                : i18n.t("verifyEmail")}
              </Text>
            </Animated.View>

            {/* Description */}
            <Animated.View entering={FadeInDown.duration(400).delay(200)}>
              <Text
              style={{
                fontSize: 16,
                textAlign: "center",
                color: "#666",
                marginBottom: hp("4%"),
                lineHeight: 24,
                paddingHorizontal: wp("2%"),
              }}
            >
              {verificationType === "password-reset"
                ? i18n.t("passwordResetVerificationDescription")
                : i18n.t("emailVerificationDescription")}
              </Text>
            </Animated.View>

            {/* Verification Code Field */}
            <Animated.View entering={FadeInDown.duration(400).delay(300)} style={{ marginBottom: hp("3%") }}>
              <Text
                style={{
                  fontSize: 16,
                  color: "#666",
                  marginBottom: hp("1%"),
                  textAlign: locale === "ar" ? "right" : "left",
                }}
              >
                {i18n.t("enterVerificationCode")}
              </Text>
              <View
                style={{
                  borderWidth: 1,
                  borderColor: "#E0E0E0",
                  borderRadius: 25,
                  backgroundColor: "#F9F9F9",
                  flexDirection: "row",
                  alignItems: "center",
                  paddingHorizontal: wp("4%"),
                  height: 50,
                }}
              >
                <Feather
                  name="key"
                  size={20}
                  color="#999"
                  style={{ marginRight: 10 }}
                />
                <TextInput
                  style={{
                    flex: 1,
                    textAlign: locale === "ar" ? "right" : "left",
                    fontSize: 16,
                    color: "#000",
                  }}
                  placeholder="123456"
                  placeholderTextColor="#999"
                  value={verificationCode}
                  onChangeText={setVerificationCode}
                  keyboardType="number-pad"
                  maxLength={6}
                  autoFocus
                />
              </View>
            </Animated.View>

            {/* Verify Button */}
            <Animated.View entering={FadeInDown.duration(400).delay(400)}>
              <TouchableOpacity
              style={{
                marginBottom: hp("2%"),
                borderRadius: 25,
                overflow: "hidden",
              }}
              onPress={handleVerifyEmail}
              disabled={buttonSpinner}
            >
              <LinearGradient
                colors={["#004E6D", "#0596D0"]}
                locations={[0, 1]}
                style={{
                  paddingVertical: hp("2%"),
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {buttonSpinner ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text
                    style={{
                      color: "white",
                      textAlign: "center",
                      fontSize: 18,
                      fontWeight: "bold",
                    }}
                  >
                    {i18n.t("verifyEmail")}
                  </Text>
                )}
              </LinearGradient>
            </TouchableOpacity>
            </Animated.View>

            {/* Resend Code Button */}
            <Animated.View entering={FadeInDown.duration(400).delay(500)} style={{ alignItems: "center", marginBottom: hp("3%") }}>
              <Text
                style={{
                  fontSize: 14,
                  color: "#666",
                  marginBottom: hp("1%"),
                }}
              >
                {i18n.t("didntReceiveCode")}
              </Text>
              <TouchableOpacity
                onPress={handleResendCode}
                disabled={resendSpinner}
              >
                {resendSpinner ? (
                  <ActivityIndicator size="small" color="#0596D0" />
                ) : (
                  <Text
                    style={{
                      color: "#0596D0",
                      fontSize: 16,
                      fontWeight: "600",
                      textDecorationLine: "underline",
                    }}
                  >
                    {i18n.t("resendCode")}
                  </Text>
                )}
                </TouchableOpacity>
              </Animated.View>

            {/* Back to Login */}
            <Animated.View entering={FadeInDown.duration(400).delay(600)} style={{ alignItems: "center", marginTop: hp("2%") }}>
              <TouchableOpacity
                onPress={() => router.push("/(routes)/(auth)/login")}
              >
                <Text
                  style={{
                    color: "#666",
                    fontSize: 14,
                  }}
                >
                  {i18n.t("backToLogin")}
                </Text>
                </TouchableOpacity>
              </Animated.View>
            </View>
          </View>
        </ScrollView>
    </LinearGradient>
  );
}
